import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cmxDateTime, commonEnums } from '@codametrix/ui-common';
import { isUnboundedDateRange } from '@codametrix/client-url-state';
import type {
  ApplyMeta,
  DateRangeValue,
  FilterPatch,
  FilterSource,
  FilterSpec,
  FilterValues,
  UrlFilterError
} from '@codametrix/client-url-state';
import { useUrlFilters } from '../../../core/url-filters/use-url-filters';
import { DEFAULT_DATE_WINDOW_DAYS } from '../../../reducers/state/cases';
import type { FilterChange } from '../../pop-up-filter/pop-up-filtersV2';

const { FilterTypes } = commonEnums;

/** Never matches a real pathname, so the hook stays inert when the view has no `basePath`. */
const NO_BASE_PATH = 'about:case-list-url-filters-disabled';

/** The one date form the case list has today. Other date types are a follow-up. */
export const DATE_SPEC_KEY = 'date';
const SUPPORTED_DATE_KEYS = ['date_of_service'];

/** Parameters other features already put on case list URLs; strict mode must not report them. */
export const CASE_LIST_ALLOWED_PARAMS = ['back', 'stage', 'returnUrl', 'runtimeOverrides'];

/** Form keys whose options load lazily, and how. */
const OPTION_LOADERS = ['case_status', 'tenant_code', 'procedure_type_category'];

type Forms = AppProps.CriteriaForm[];

const isDateForm = (form: AppProps.CriteriaForm): boolean =>
  Boolean(form.isDate) || SUPPORTED_DATE_KEYS.includes(form.key);

const isOwned = (form: AppProps.CriteriaForm): boolean =>
  !form.disabled && !form.isSelect;

/** Selectable values of a form: every key in its field tree. Tenants are a tree of organisations. */
export const optionKeys = (form: AppProps.CriteriaForm): string[] => {
  const keys: string[] = [];
  const visit = (fields?: CMxCommonApp.FieldDefinition[]) => {
    fields?.forEach(field => {
      if (field?.key && field.key !== 'invalid') {
        keys.push(field.key);
      }
      visit(field?.children);
    });
  };
  visit(form.formDefinition?.fieldGroups?.[0]?.fields);
  return keys;
};

const formsLoaded = (
  form: AppProps.CriteriaForm,
  filterLoadStates: Record<string, CMxCommonApp.Loading> | undefined
): boolean =>
  optionKeys(form).length > 0 ||
  filterLoadStates?.[form.key]?.loading === false;

/** The case list's default service date range (the last 30 days), as the reducer sets it. */
export const defaultDateRange = (): DateRangeValue => ({
  key: 'date_of_service',
  from: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE, {
    throw: false,
    localized: true,
    offset: {
      action: cmxDateTime.DateManipulateOperation.SUBTRACT,
      delta: [DEFAULT_DATE_WINDOW_DAYS - 1, cmxDateTime.DateManipulateUnit.DAY]
    }
  }),
  to: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE)
});

/**
 * A form whose load finished or failed with no options: there is nothing to validate URL values
 * against. Reported once as a load problem, not as an invalid value for each URL value.
 */
const optionsUnavailable = (
  form: AppProps.CriteriaForm,
  filterLoadStates: Record<string, CMxCommonApp.Loading> | undefined
): boolean =>
  optionKeys(form).length === 0 &&
  (filterLoadStates?.[form.key]?.loading === false ||
    filterLoadStates?.[form.key]?.error === true);

/**
 * The keys this view owns, derived from the enabled filter forms (never a hard-coded list). A
 * disabled form (for example `customer_phase` outside professional contexts) is not owned, so a
 * URL cannot override a forced filter. Defaults: the date range is the last 30 days; every
 * option key defaults to no selection.
 */
export const buildCaseListSpecs = (
  forms: Forms,
  filterLoadStates?: Record<string, CMxCommonApp.Loading>
): FilterSpec[] => {
  const specs: FilterSpec[] = [];
  forms.filter(isOwned).forEach(form => {
    if (isDateForm(form)) {
      if (!specs.some(spec => spec.key === DATE_SPEC_KEY)) {
        specs.push({
          key: DATE_SPEC_KEY,
          kind: 'dateRange',
          default: defaultDateRange(),
          validate: dateKey => SUPPORTED_DATE_KEYS.includes(dateKey)
        });
      }
      return;
    }
    // With no options available the load problem is reported once (see the hook); values are not
    // each reported as invalid.
    const loaded =
      formsLoaded(form, filterLoadStates) &&
      !optionsUnavailable(form, filterLoadStates);
    const options = new Set(optionKeys(form));
    specs.push({
      key: form.key,
      kind: 'multi',
      default: [],
      // Until options have loaded there is nothing to validate against.
      validate: value => !loaded || options.has(value)
    });
  });
  return specs;
};

interface CurrentCriteria {
  filters?: CMxCommonApp.Filter[];
  dateFilter?: CMxCommonApp.DateFilter;
}

interface Criteria {
  filters: CMxCommonApp.Filter[];
  dateFilter?: CMxCommonApp.DateFilter;
  changedFilters: boolean;
  changedDate: boolean;
}

const sameFilters = (
  a: CMxCommonApp.Filter[],
  b: CMxCommonApp.Filter[]
): boolean => {
  const normal = (list: CMxCommonApp.Filter[]) =>
    JSON.stringify(
      list
        .map(filter => ({
          key: filter.key,
          type: filter.type,
          terms: [...filter.terms].sort()
        }))
        .sort((x, y) => x.key.localeCompare(y.key))
    );
  return normal(a) === normal(b);
};

const sameDate = (
  a?: CMxCommonApp.DateFilter,
  b?: CMxCommonApp.DateFilter
): boolean => a?.key === b?.key && a?.from === b?.from && a?.to === b?.to;

/**
 * Turns the resolved URL values into the criteria the existing widgets and list request already
 * understand. Every owned key is taken from `values`, so a key absent from the URL resolves to its
 * default: the URL, not earlier widget state, decides. `ALL` expands to every loaded option and
 * a blank value clears that key's filter. Filters the view does not own (search, forced phase)
 * are kept.
 */
export const toCriteria = (
  values: FilterValues,
  forms: Forms,
  current: CurrentCriteria
): Criteria => {
  // Date range clauses are rebuilt from `dateFilter` when the list request is made.
  const kept = (current.filters ?? []).filter(
    filter =>
      filter.type !== 'GREATER_THAN_EQUAL' && filter.type !== 'LESS_THAN_EQUAL'
  );
  let filters = kept;
  let dateFilter = current.dateFilter;

  forms.filter(isOwned).forEach(form => {
    if (isDateForm(form)) {
      const range = values[DATE_SPEC_KEY] as DateRangeValue | undefined;
      if (range) {
        // A blank range means all dates: remove the date restriction.
        dateFilter = isUnboundedDateRange(range)
          ? undefined
          : ({
              key: range.key,
              type: FilterTypes.BETWEEN,
              from: range.from,
              to: range.to
            } as CMxCommonApp.DateFilter);
      }
      return;
    }
    const value = values[form.key];
    const terms = Array.isArray(value)
      ? value
      : value && typeof value === 'object' && 'all' in value
      ? optionKeys(form)
      : [];
    filters = filters.filter(filter => filter.key !== form.key);
    if (terms.length > 0) {
      filters = [...filters, { key: form.key, terms, type: FilterTypes.IN }];
    }
  });

  return {
    filters,
    dateFilter,
    changedFilters: !sameFilters(filters, kept),
    changedDate: !sameDate(dateFilter, current.dateFilter)
  };
};

/**
 * What to write into the URL so it reflects the current widget state. Used once, to adopt state
 * that already exists (for example filters kept in Redux from an earlier visit) when the page is
 * opened without any filter parameters.
 */
export const toAdoptionPatch = (
  forms: Forms,
  current: CurrentCriteria
): FilterPatch => {
  const patch: FilterPatch = {};
  forms.filter(isOwned).forEach(form => {
    if (isDateForm(form)) {
      const date = current.dateFilter;
      const fallback = defaultDateRange();
      if (!date) {
        // State with no date filter is "all dates" (the initial state always has one): write it
        // so the URL does not claim the default range.
        patch[DATE_SPEC_KEY] = { key: fallback.key, from: '', to: '' };
      } else if (
        (date.key !== fallback.key ||
          date.from !== fallback.from ||
          date.to !== fallback.to) &&
        SUPPORTED_DATE_KEYS.includes(date.key)
      ) {
        patch[DATE_SPEC_KEY] = { key: date.key, from: date.from, to: date.to };
      }
      return;
    }
    const filter = (current.filters ?? []).find(f => f.key === form.key);
    if (filter && filter.terms.length > 0) {
      patch[form.key] = filter.terms;
    }
  });
  return patch;
};

/** Maps what a filter widget reports onto the URL filter it owns. */
export const toPatch = (change: FilterChange, forms: Forms): FilterPatch => {
  if (change.kind === 'clearDate') {
    return { [DATE_SPEC_KEY]: { key: change.key, from: '', to: '' } };
  }
  if (change.kind === 'date') {
    return {
      [DATE_SPEC_KEY]: { key: change.key, from: change.from, to: change.to }
    };
  }
  const form = forms.find(f => f.key === change.key);
  const all = form ? optionKeys(form) : [];
  const selected = new Set(change.values);
  const isAll =
    all.length > 0 &&
    selected.size === all.length &&
    all.every(key => selected.has(key));
  return { [change.key]: isAll ? { all: true } : change.values };
};

export interface UseCaseListUrlFiltersArgs {
  /** Concrete list path, e.g. `/cmx/cases/bedside-pro/`. Omit to disable. */
  basePath?: string;
  criteriaFilter: AppProps.CriteriaFilter;
  filterLoadStates?: Record<string, CMxCommonApp.Loading>;
  activeOrgId: number;
  getDictionary?: (params: AppProps.CategoryParams) => void;
  getStatuses?: (params: AppProps.CategoryParams) => void;
  getTenants?: (organizationId: number) => void;
  /** Local list state that feeds the grid synchronously, before Redux settles. */
  setFilterableOptions: (options: CMxCommonApp.FilterableOption) => void;
  applyFilters: (filters: CMxCommonApp.Filter[]) => void;
  /** `undefined` removes the date restriction (all dates). */
  applyDateFilter: (dateFilter: CMxCommonApp.DateFilter | undefined) => void;
}

export interface CaseListUrlFilters {
  /** `true` when the view manages filters through the URL (it has a `basePath`). */
  enabled: boolean;
  /** `true` when the grid may load: the URL is not for this view, or its filters have been applied. */
  gateOpen: boolean;
  /** Problems with the incoming URL; the list is not shown while these exist. */
  errors: UrlFilterError[];
  /** Why the last change was not written (URL too long); the current results stay. */
  writeError: UrlFilterError | null;
  /** Pass to `PopUpFiltersV2`: widget changes go to the URL instead of being dispatched. */
  onFiltersChange: (change: FilterChange) => void;
  /**
   * Restores the defaults of the option filters and shows all dates: the date range is written
   * blank because an absent range would mean the default last-three-days range.
   */
  clearFilters: () => void;
}

/**
 * URL-first filters for the case list. A widget change writes the URL; the URL change calls
 * `apply`, which updates the existing Redux filter state and the grid's request. The same path
 * serves pasted and bookmarked links, drill-through links, and back/forward.
 */
export const useCaseListUrlFilters = (
  args: UseCaseListUrlFiltersArgs
): CaseListUrlFilters => {
  const {
    basePath,
    criteriaFilter,
    filterLoadStates,
    activeOrgId,
    getDictionary,
    getStatuses,
    getTenants
  } = args;
  const { search } = useLocation();
  const forms = criteriaFilter.filterForms;

  const specs = useMemo(
    () => buildCaseListSpecs(forms, filterLoadStates),
    [forms, filterLoadStates]
  );

  // Option-backed keys the URL mentions need their options loaded before values can be validated.
  const urlKeys = useMemo(() => {
    const params = new URLSearchParams(search);
    return OPTION_LOADERS.filter(key => params.has(key));
  }, [search]);

  const requested = useRef(new Set<string>());
  useEffect(() => {
    urlKeys.forEach(key => {
      const form = forms.find(f => f.key === key);
      if (!form || form.disabled || requested.current.has(key)) {
        return;
      }
      if (formsLoaded(form, filterLoadStates)) {
        return;
      }
      requested.current.add(key);
      if (key === 'case_status') {
        getStatuses?.({ categoryName: 'casestatus', key: 'case_status' });
      } else if (key === 'tenant_code') {
        getTenants?.(activeOrgId);
      } else if (key === 'procedure_type_category') {
        getDictionary?.({
          categoryName: 'Procedure Type Categories',
          key: 'procedure_type_category'
        });
      }
    });
  }, [urlKeys, forms, filterLoadStates, activeOrgId, getDictionary, getStatuses, getTenants]);

  const ready = urlKeys.every(key => {
    const form = forms.find(f => f.key === key);
    return !form || form.disabled || formsLoaded(form, filterLoadStates);
  });

  const latest = useRef({ ...args, forms });
  latest.current = { ...args, forms };

  // The first apply on a page opened with no filter parameters keeps whatever state already
  // exists (Redux keeps filters across navigation) and then adopts it into the URL, so the URL
  // is true from the start and the previous behaviour is preserved.
  const adoptPending = useRef(false);
  const [adoptTick, setAdoptTick] = useState(0);

  const apply = useCallback(
    (
      values: FilterValues,
      sources: Record<string, FilterSource>,
      meta: ApplyMeta
    ) => {
      const current = latest.current;
      // A URL with no filter parameters keeps the existing state, and writes it into the URL:
      // on mount (Redux keeps filters across navigation) and when the user follows a link to the
      // bare list path (for example the sidebar Cases link). Back and forward to a bare URL
      // restore the defaults, and the hook's own writes are applied as written.
      const bare = !Object.values(sources).some(source => source === 'url');
      if (bare && (meta.initial || (!meta.selfInitiated && meta.action !== 'POP'))) {
        adoptPending.current = true;
        setAdoptTick(tick => tick + 1);
        return;
      }
      const criteria = toCriteria(values, current.forms, {
        filters: current.criteriaFilter.filters,
        dateFilter: current.criteriaFilter.dateFilter
      });
      if (!criteria.changedFilters && !criteria.changedDate) {
        return;
      }
      current.setFilterableOptions({
        dateFilter: criteria.dateFilter,
        filters: criteria.filters
      });
      if (criteria.changedFilters) {
        current.applyFilters(criteria.filters);
      }
      if (criteria.changedDate) {
        current.applyDateFilter(criteria.dateFilter);
      }
    },
    []
  );

  const {
    errors,
    writeError,
    active,
    applied,
    setFilters
  } = useUrlFilters({
    specs,
    basePath: basePath ?? NO_BASE_PATH,
    apply,
    ready,
    strict: true,
    allow: CASE_LIST_ALLOWED_PARAMS
  });

  // A key in the URL whose options could not be loaded cannot be checked: say so, and hold the list.
  const loadErrors = useMemo<UrlFilterError[]>(
    () =>
      urlKeys.flatMap(key => {
        const form = forms.find(f => f.key === key);
        return form && !form.disabled && optionsUnavailable(form, filterLoadStates)
          ? [
              {
                code: 'invalid-value' as const,
                key,
                message: `The options for "${key}" could not be loaded, so this link can't be checked`
              }
            ]
          : [];
      }),
    [urlKeys, forms, filterLoadStates]
  );
  const allErrors = useMemo(() => [...errors, ...loadErrors], [errors, loadErrors]);

  useEffect(() => {
    if (!applied || !adoptPending.current) {
      return;
    }
    adoptPending.current = false;
    // A URL with problems is shown as such; never overwrite it with existing state.
    if (allErrors.length > 0) {
      return;
    }
    const patch = toAdoptionPatch(forms, {
      filters: criteriaFilter.filters,
      dateFilter: criteriaFilter.dateFilter
    });
    if (Object.keys(patch).length > 0) {
      setFilters(patch, { replace: true });
    }
  }, [applied, adoptTick, allErrors, forms, criteriaFilter.filters, criteriaFilter.dateFilter, setFilters]);

  const onFiltersChange = useCallback(
    (change: FilterChange) => {
      const patch = toPatch(change, latest.current.forms);
      if (Object.keys(patch).every(key => specs.some(spec => spec.key === key))) {
        setFilters(patch);
      }
    },
    [setFilters, specs]
  );

  return {
    enabled: Boolean(basePath),
    gateOpen: !active || applied,
    errors: active ? allErrors : [],
    writeError: active ? writeError : null,
    onFiltersChange,
    clearFilters: useCallback(() => {
      const patch: FilterPatch = {};
      specs.forEach(spec => {
        patch[spec.key] =
          spec.kind === 'dateRange'
            ? { key: defaultDateRange().key, from: '', to: '' }
            : null;
      });
      setFilters(patch);
    }, [specs, setFilters])
  };
};
